import { Component, inject, input, OnInit, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { noWhitespaceValidator } from "../../../../core/validators";
import { FormInputComponent } from "../../../../shared/components";
import { PageMode } from "../../../../shared/constants/enums";
import { PillarFocus } from "../../../../shared/models";
import { LanguageService } from "../../../../shared/services";

@Component({
  selector: "adaa-area-of-focus-modal",
  imports: [FormInputComponent, TranslatePipe, ReactiveFormsModule],
  templateUrl: "./area-of-focus-modal.html",
  styleUrl: "./area-of-focus-modal.scss",
})
export class AreaOfFocusModalComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _modalService = inject(NgbModal);
  private _languageService = inject(LanguageService);
  private _toastrService = inject(ToastrService);

  areaOfFocusForm: FormGroup = new FormGroup({});
  submitted = signal<boolean>(false);
  modal = viewChild.required<TemplateRef<unknown>>("content");
  pageMode = input<string>();
  focusList = input.required<PillarFocus[]>();
  focus = output<PillarFocus>();
  modalRef: NgbModalRef;
  PageMode = PageMode;
  focusIndex: number | null;

  public ngOnInit(): void {
    this._prepareForm();
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.areaOfFocusForm = this._formBuilder.group({
      areaNameAE: this._formBuilder.control<string>(
        { value: "", disabled: isViewMode },
        {
          validators: [Validators.required, noWhitespaceValidator()],
        }
      ),
      areaNameEN: this._formBuilder.control<string>(
        { value: "", disabled: isViewMode },
        {
          validators: [Validators.required, noWhitespaceValidator()],
        }
      ),
      areaDscAE: this._formBuilder.control<string>({ value: "", disabled: isViewMode }),
      areaDscEN: this._formBuilder.control<string>({ value: "", disabled: isViewMode }),
    });
  }

  public open() {
    const modalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._languageService.modalDirection(),
    });

    this.modalRef = modalRef;
    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  public submit() {
    this.submitted.set(true);
    if (!this._checkUnique("areaNameAE") || !this._checkUnique("areaNameEN")) {
      this._toastrService.error(this._languageService.translate("notification.error.focus_not_unique"));
      return;
    }
    if (this.areaOfFocusForm.invalid) {
      this._toastrService.error(this._languageService.translate("notification.warning.missing_info"));
      return;
    }

    this.focus.emit({
      ...this.focusList()[this.focusIndex as number],
      ...this.areaOfFocusForm.value,
    });
    this.modalRef.close();
  }

  private _checkUnique(control: "areaNameAE" | "areaNameEN"): boolean {
    const value = this.areaOfFocusForm.get(control)?.value;
    const index = this.focusList()?.findIndex((focus) => focus[control] === value);

    if (index > -1 && index !== this.focusIndex) return false;
    else return true;
  }

  private _resetForm() {
    this.areaOfFocusForm.reset();
    this.areaOfFocusForm.markAsPristine();
    this.areaOfFocusForm.markAsUntouched();
    this.focusIndex = null;
    this.submitted.set(false);
  }
}
